import React, { useEffect, useState } from 'react';
import { FinancingType, isForeignBrandOrder } from 'next-common';
import { FinancingForm } from './FinancingForm';
import { ServiceAgreementForm } from './ServiceAgreementForm';

import { CustomModal, IconButton, IconType, LoadingSpinner } from '@repo/ui/components';
import { AgreementsComparisonList } from './AgreementsComparisonList';
import { usedCar, useDisableScroll } from '../../../../common';
import { AlternativeFinancing } from '../FinanceAccordion/AlternativeFinancing';
import { serviceContractDisabledCodes } from '../../../../utils';
import { cn, formatNumber } from 'types';
import { selectServiceContractLoading } from '../../../../store/selectors';
import { useAppSelector } from '../../../../store/hooks';

export const FinancingTabSection = (props) => {
  const [showServiceContract, setShowServiceContract] = useState(!!props.serviceContractDb);
  const [active, setActive] = useState<boolean>(false);
  const isUsedCar = usedCar();
  const isForeignBrand = isForeignBrandOrder(props.order);
  const serviceContractLoading = useAppSelector(selectServiceContractLoading);

  useDisableScroll(active);

  useEffect(() => {
    setShowServiceContract(!!props?.serviceContractDb);
  }, [props?.serviceContractDb]);

  const isShowServiceContractButton = () =>
    !isUsedCar &&
    !isForeignBrand &&
    !serviceContractDisabledCodes.includes(props?.order?.pimSnapshot?.vehicleUsageCode);

  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-x-4',
        (props.financingType || props.type) !== FinancingType.CASH &&
          (isUsedCar || isForeignBrand) &&
          props.type !== FinancingType.ALTERNATIVELEASING
          ? 'grid-cols-1'
          : ''
      )}
    >
      {(props.type !== FinancingType.CASH && props.type !== FinancingType.ALTERNATIVELEASING) || props.isSigningStep ? (
        <div className="rounded-lg bg-white p-6 border border-neutralGrey flex flex-col gap-6">
          <FinancingForm
            loading={props?.loading}
            setAddNew={props?.setAddNew}
            offerId={props?.offerId}
            dialogTitle={props?.dialogTitle}
            onUpdateCalculation={props.onUpdateCalculation}
            calculation={props.calculation}
            vehicleConfig={props.vehicleConfig}
            type={props.financingType || props.type}
            customerType={props.customerType}
            isSaving={props.isSaving}
            isEditable={props.isEditable}
            isModal={false}
            hideComments={props.isCash}
            onAddComment={props.handleAddComment}
            order={props.order}
            addNew={props?.addNew}
            onAddCalculation={props?.onAddCalculation}
            isDeleted={props.isDeleted}
            configuration={props?.configuration}
            onDeleteCalculation={props?.onDeleteCalculation}
            isStockSaleOrder={props.isStockSaleOrder}
            onAlertModalToggle={props?.onAlertModalToggle}
            setLeasingInput={props?.setLeasingInput}
          />
        </div>
      ) : null}

      {props.type === FinancingType.ALTERNATIVELEASING && (
        <AlternativeFinancing
          leasingCompanies={props?.leasingCompanies}
          loading={props?.loading}
          isSaving={props?.loading}
          order={props?.order}
          onAlertModalToggle={props?.onAlertModalToggle}
          setLeasingInput={props?.setLeasingInput}
          onAddCalculation={props?.onAddCalculation}
          removeAlternativeLeasing={props?.removeAlternativeLeasing}
          setAddNew={props?.setAddNew}
        />
      )}

      {props.type === FinancingType.CASH && (
        <div className="flex flex-col gap-6 rounded-lg border border-neutralGrey p-6 bg-white">
          <div className="m-0 text-lg/[175%] font-extrabold">Kontant</div>
          <div className="flex gap-6 w-full">
            <span className="flex flex-1 text-base/[175%] font-semibold">Totalpris </span>
            <span className="text-2xl/[165%] font-extrabold">
              {typeof props.calculation?.financing?.totalCost === 'number'
                ? `${formatNumber(props.calculation?.financing?.totalCost)} kr`
                : 'ukjent'}
            </span>
          </div>
        </div>
      )}

      {serviceContractLoading && (
        <div className="rounded-lg bg-white p-6 border border-neutralGrey flex items-center justify-center w-full h-full">
          <LoadingSpinner className="" />
        </div>
      )}
      {!serviceContractLoading &&
        isShowServiceContractButton() &&
        (!showServiceContract && !props?.serviceContractDb?.type ? (
          <div
            className={cn(
              'rounded-lg bg-white p-6 border border-neutralGrey flex items-center justify-center relative',
              props.type === FinancingType.CASH ? 'min-h-[15rem]' : 'min-h-[23rem]'
            )}
          >
            <div
              onClick={() =>
                !(props.calculation ? false : props.addNew) && setShowServiceContract(!showServiceContract)
              }
              className="cursor-pointer flex flex-col items-center justify-center gap-2"
            >
              <IconButton
                id="addServiceContractButton"
                className="bg-turquoise rounded-full p-0.5"
                ariaLabel={'plus-knapp'}
                icon={IconType.Plus}
                disabled={!props?.calculation || props.isUnavailablePimData}
                onClick={() => setShowServiceContract(!showServiceContract)}
                height={24}
                width={24}
                fill={
                  !props?.calculation || props.isUnavailablePimData ? 'var(--color-neutralPlus)' : 'var(--color-black)'
                }
              />
              <label
                htmlFor="addServiceContractButton"
                className={cn(
                  'text-lg/[175%] font-extrabold',
                  !props?.calculation || props.isUnavailablePimData ? 'text-neutralPlus' : ''
                )}
              >
                Legg til serviceavtale
              </label>
            </div>
            <div className="absolute bottom-6">
              <a
                onClick={() => setActive(!active)}
                className="no-underline cursor-pointer ml-1 text-lg font-semibold border-b-2 border-turquoise"
              >
                <span className="text-base/[175%] font-semibold">Sammenlign serviceavtaler</span>
              </a>
            </div>
          </div>
        ) : (
          <div className="rounded-lg bg-white p-6 border border-neutralGrey min-h-[23rem]">
            {!props?.serviceContract || !props?.serviceContract?.contractTypeList?.length ? (
              <p>Servicekontrakter er ikke tilgjengelig for denne modellen</p>
            ) : (
              <ServiceAgreementForm
                setShowServiceContractComparisionModal={setActive}
                setShowServiceContract={setShowServiceContract}
                offerId={props?.offerId ?? null}
                onUpdateServiceContract={props.onUpdateServiceContract}
                calculation={props.calculation}
                serviceContractDb={props.serviceContractDb}
                serviceContract={props.serviceContract}
                vehicleConfig={props.vehicleConfig}
                type={props.financingType}
                customerType={props.customerType}
                isSaving={props.isSaving}
                isEditable={props.isEditable}
                isModal={false}
                hideComments={props.isCash}
                order={props.order}
                isUnavailablePimData={props.isUnavailablePimData}
              />
            )}
          </div>
        ))}

      {!isUsedCar && !isForeignBrand && (
        <CustomModal
          open={active}
          onCloseClick={() => setActive(!active)}
          headerTitle="Serviceavtaler"
          className="max-w-[68.75rem] w-full"
        >
          <AgreementsComparisonList serviceContract={props?.serviceContract} />
        </CustomModal>
      )}
    </div>
  );
};
